/**
 * Icon Resolution Utilities
 * Resolves string icon names to Lucide React components
 *
 * This module provides:
 * - Icon resolution from string names via a STATIC map (the icons the app's
 *   navigation seeds, settings, notifications and the admin picker's curated
 *   lists use), so the root layout ships ~200 icons, not all ~1,480
 * - Icon name normalization (kebab-case, PascalCase)
 * - Caching for performance
 * - A lazy full Lucide set (./iconsFull) that loads only when a name is not in
 *   the static map (an admin-picked exotic icon) or when the admin icon picker
 *   opens. Behaviour for any name is unchanged; only WHEN the rest loads moves.
 * - Icon name listing for admin UI
 *
 * Do NOT `import * as ... from 'lucide-react'` here: it puts every icon in the
 * initial JS of every route (518 KB raw / 139 KB gzip). The guard test
 * lib/navigation/icons.static.test.ts fails if it comes back.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Activity as IActivity,
  AlertCircle as IAlertCircle,
  AlertOctagon as IAlertOctagon,
  AlertTriangle as IAlertTriangle,
  AlignLeft as IAlignLeft,
  ArrowDown as IArrowDown,
  ArrowLeft as IArrowLeft,
  ArrowRight as IArrowRight,
  ArrowUp as IArrowUp,
  ArrowUpDown as IArrowUpDown,
  AtSign as IAtSign,
  Award as IAward,
  BarChart as IBarChart,
  BarChart2 as IBarChart2,
  BarChart3 as IBarChart3,
  Beaker as IBeaker,
  Bell as IBell,
  BellOff as IBellOff,
  Book as IBook,
  BookOpen as IBookOpen,
  Bookmark as IBookmark,
  Box as IBox,
  Brain as IBrain,
  Briefcase as IBriefcase,
  Building as IBuilding,
  Building2 as IBuilding2,
  Calculator as ICalculator,
  Calendar as ICalendar,
  CalendarDays as ICalendarDays,
  Check as ICheck,
  CheckCircle as ICheckCircle,
  CheckCircle2 as ICheckCircle2,
  ChevronDown as IChevronDown,
  ChevronLeft as IChevronLeft,
  ChevronRight as IChevronRight,
  ChevronUp as IChevronUp,
  CircleSlash as ICircleSlash,
  Clipboard as IClipboard,
  ClipboardCheck as IClipboardCheck,
  ClipboardList as IClipboardList,
  Clock as IClock,
  Cloud as ICloud,
  CloudOff as ICloudOff,
  Code as ICode,
  Cog as ICog,
  Compass as ICompass,
  Contact as IContact,
  Contact2 as IContact2,
  Copy as ICopy,
  CornerUpLeft as ICornerUpLeft,
  CornerUpRight as ICornerUpRight,
  Cpu as ICpu,
  CreditCard as ICreditCard,
  Crown as ICrown,
  Database as IDatabase,
  DollarSign as IDollarSign,
  Download as IDownload,
  Edit as IEdit,
  Edit2 as IEdit2,
  Edit3 as IEdit3,
  ExternalLink as IExternalLink,
  Eye as IEye,
  EyeOff as IEyeOff,
  File as IFile,
  FileCheck as IFileCheck,
  FileCode as IFileCode,
  FileMinus as IFileMinus,
  FileOutput as IFileOutput,
  FilePlus as IFilePlus,
  FileSearch as IFileSearch,
  FileStack as IFileStack,
  FileText as IFileText,
  FileVideo as IFileVideo,
  FileX as IFileX,
  Film as IFilm,
  Filter as IFilter,
  Fingerprint as IFingerprint,
  Flame as IFlame,
  Folder as IFolder,
  FolderMinus as IFolderMinus,
  FolderOpen as IFolderOpen,
  FolderPlus as IFolderPlus,
  Gauge as IGauge,
  GitBranch as IGitBranch,
  GitCompare as IGitCompare,
  GitMerge as IGitMerge,
  Globe as IGlobe,
  Globe2 as IGlobe2,
  GraduationCap as IGraduationCap,
  Grid as IGrid,
  Grid3x3 as IGrid3x3,
  Heart as IHeart,
  HelpCircle as IHelpCircle,
  Home as IHome,
  Image as IImage,
  Inbox as IInbox,
  Info as IInfo,
  Key as IKey,
  KeyRound as IKeyRound,
  Landmark as ILandmark,
  Laptop as ILaptop,
  Layers as ILayers,
  Layout as ILayout,
  LayoutDashboard as ILayoutDashboard,
  LayoutGrid as ILayoutGrid,
  LayoutList as ILayoutList,
  Library as ILibrary,
  Lightbulb as ILightbulb,
  LineChart as ILineChart,
  Link2 as ILink2,
  List as IList,
  Loader as ILoader,
  Loader2 as ILoader2,
  Locate as ILocate,
  LocateFixed as ILocateFixed,
  LocateOff as ILocateOff,
  Lock as ILock,
  Mail as IMail,
  Map as IMap,
  MapPin as IMapPin,
  Maximize as IMaximize,
  Maximize2 as IMaximize2,
  Menu as IMenu,
  MessageCircle as IMessageCircle,
  MessageSquare as IMessageSquare,
  Mic as IMic,
  Microscope as IMicroscope,
  Minimize as IMinimize,
  Minimize2 as IMinimize2,
  Minus as IMinus,
  Monitor as IMonitor,
  MoreHorizontal as IMoreHorizontal,
  MoreVertical as IMoreVertical,
  MousePointer as IMousePointer,
  Music as IMusic,
  Navigation as INavigation,
  Navigation2 as INavigation2,
  Paintbrush as IPaintbrush,
  Palette as IPalette,
  PenTool as IPenTool,
  Percent as IPercent,
  Phone as IPhone,
  PhoneCall as IPhoneCall,
  PhoneIncoming as IPhoneIncoming,
  PhoneOutgoing as IPhoneOutgoing,
  PieChart as IPieChart,
  Play as IPlay,
  Plug as IPlug,
  Plus as IPlus,
  Radio as IRadio,
  Receipt as IReceipt,
  RefreshCw as IRefreshCw,
  Rocket as IRocket,
  RotateCcw as IRotateCcw,
  RotateCw as IRotateCw,
  Save as ISave,
  ScrollText as IScrollText,
  Search as ISearch,
  Send as ISend,
  Server as IServer,
  Settings as ISettings,
  Settings2 as ISettings2,
  Share as IShare,
  Share2 as IShare2,
  Shield as IShield,
  ShieldCheck as IShieldCheck,
  ShieldOff as IShieldOff,
  Sliders as ISliders,
  Smartphone as ISmartphone,
  SortAsc as ISortAsc,
  SortDesc as ISortDesc,
  Sparkles as ISparkles,
  Star as IStar,
  StickyNote as IStickyNote,
  Tablet as ITablet,
  Tag as ITag,
  Tags as ITags,
  Target as ITarget,
  Terminal as ITerminal,
  Timer as ITimer,
  Trash as ITrash,
  Trash2 as ITrash2,
  TrendingDown as ITrendingDown,
  TrendingUp as ITrendingUp,
  Trophy as ITrophy,
  Unlock as IUnlock,
  Upload as IUpload,
  User as IUser,
  UserCheck as IUserCheck,
  UserCog as IUserCog,
  UserMinus as IUserMinus,
  UserPlus as IUserPlus,
  UserX as IUserX,
  Users as IUsers,
  Video as IVideo,
  VideoOff as IVideoOff,
  Wallet as IWallet,
  Webhook as IWebhook,
  Wifi as IWifi,
  WifiOff as IWifiOff,
  Wrench as IWrench,
  X as IX,
  XCircle as IXCircle,
  Zap as IZap,
} from 'lucide-react';
import { customIcons } from '@/components/icons/MultiLineChart';

// ============================================================================
// Static icon map
// ============================================================================

/**
 * Icons resolvable synchronously without the full Lucide set. Keep every icon
 * name used by a navigation seed/migration, lib/settings/categories.ts,
 * notification category/page metadata, DEVELOPER_ICONS and the admin picker's
 * curated lists here. icons.static.test.ts checks the seeds against it.
 * Names that Lucide does not export (e.g. 'Refresh') are deliberately absent:
 * they resolved to null before and still do.
 */
const STATIC_ICONS: Record<string, LucideIcon> = {
  Activity: IActivity,
  AlertCircle: IAlertCircle,
  AlertOctagon: IAlertOctagon,
  AlertTriangle: IAlertTriangle,
  AlignLeft: IAlignLeft,
  ArrowDown: IArrowDown,
  ArrowLeft: IArrowLeft,
  ArrowRight: IArrowRight,
  ArrowUp: IArrowUp,
  ArrowUpDown: IArrowUpDown,
  AtSign: IAtSign,
  Award: IAward,
  BarChart: IBarChart,
  BarChart2: IBarChart2,
  BarChart3: IBarChart3,
  Beaker: IBeaker,
  Bell: IBell,
  BellOff: IBellOff,
  Book: IBook,
  BookOpen: IBookOpen,
  Bookmark: IBookmark,
  Box: IBox,
  Brain: IBrain,
  Briefcase: IBriefcase,
  Building: IBuilding,
  Building2: IBuilding2,
  Calculator: ICalculator,
  Calendar: ICalendar,
  CalendarDays: ICalendarDays,
  Check: ICheck,
  CheckCircle: ICheckCircle,
  CheckCircle2: ICheckCircle2,
  ChevronDown: IChevronDown,
  ChevronLeft: IChevronLeft,
  ChevronRight: IChevronRight,
  ChevronUp: IChevronUp,
  CircleSlash: ICircleSlash,
  Clipboard: IClipboard,
  ClipboardCheck: IClipboardCheck,
  ClipboardList: IClipboardList,
  Clock: IClock,
  Cloud: ICloud,
  CloudOff: ICloudOff,
  Code: ICode,
  Cog: ICog,
  Compass: ICompass,
  Contact: IContact,
  Contact2: IContact2,
  Copy: ICopy,
  CornerUpLeft: ICornerUpLeft,
  CornerUpRight: ICornerUpRight,
  Cpu: ICpu,
  CreditCard: ICreditCard,
  Crown: ICrown,
  Database: IDatabase,
  DollarSign: IDollarSign,
  Download: IDownload,
  Edit: IEdit,
  Edit2: IEdit2,
  Edit3: IEdit3,
  ExternalLink: IExternalLink,
  Eye: IEye,
  EyeOff: IEyeOff,
  File: IFile,
  FileCheck: IFileCheck,
  FileCode: IFileCode,
  FileMinus: IFileMinus,
  FileOutput: IFileOutput,
  FilePlus: IFilePlus,
  FileSearch: IFileSearch,
  FileStack: IFileStack,
  FileText: IFileText,
  FileVideo: IFileVideo,
  FileX: IFileX,
  Film: IFilm,
  Filter: IFilter,
  Fingerprint: IFingerprint,
  Flame: IFlame,
  Folder: IFolder,
  FolderMinus: IFolderMinus,
  FolderOpen: IFolderOpen,
  FolderPlus: IFolderPlus,
  Gauge: IGauge,
  GitBranch: IGitBranch,
  GitCompare: IGitCompare,
  GitMerge: IGitMerge,
  Globe: IGlobe,
  Globe2: IGlobe2,
  GraduationCap: IGraduationCap,
  Grid: IGrid,
  Grid3x3: IGrid3x3,
  Heart: IHeart,
  HelpCircle: IHelpCircle,
  Home: IHome,
  Image: IImage,
  Inbox: IInbox,
  Info: IInfo,
  Key: IKey,
  KeyRound: IKeyRound,
  Landmark: ILandmark,
  Laptop: ILaptop,
  Layers: ILayers,
  Layout: ILayout,
  LayoutDashboard: ILayoutDashboard,
  LayoutGrid: ILayoutGrid,
  LayoutList: ILayoutList,
  Library: ILibrary,
  Lightbulb: ILightbulb,
  LineChart: ILineChart,
  Link2: ILink2,
  List: IList,
  Loader: ILoader,
  Loader2: ILoader2,
  Locate: ILocate,
  LocateFixed: ILocateFixed,
  LocateOff: ILocateOff,
  Lock: ILock,
  Mail: IMail,
  Map: IMap,
  MapPin: IMapPin,
  Maximize: IMaximize,
  Maximize2: IMaximize2,
  Menu: IMenu,
  MessageCircle: IMessageCircle,
  MessageSquare: IMessageSquare,
  Mic: IMic,
  Microscope: IMicroscope,
  Minimize: IMinimize,
  Minimize2: IMinimize2,
  Minus: IMinus,
  Monitor: IMonitor,
  MoreHorizontal: IMoreHorizontal,
  MoreVertical: IMoreVertical,
  MousePointer: IMousePointer,
  Music: IMusic,
  Navigation: INavigation,
  Navigation2: INavigation2,
  Paintbrush: IPaintbrush,
  Palette: IPalette,
  PenTool: IPenTool,
  Percent: IPercent,
  Phone: IPhone,
  PhoneCall: IPhoneCall,
  PhoneIncoming: IPhoneIncoming,
  PhoneOutgoing: IPhoneOutgoing,
  PieChart: IPieChart,
  Play: IPlay,
  Plug: IPlug,
  Plus: IPlus,
  Radio: IRadio,
  Receipt: IReceipt,
  RefreshCw: IRefreshCw,
  Rocket: IRocket,
  RotateCcw: IRotateCcw,
  RotateCw: IRotateCw,
  Save: ISave,
  ScrollText: IScrollText,
  Search: ISearch,
  Send: ISend,
  Server: IServer,
  Settings: ISettings,
  Settings2: ISettings2,
  Share: IShare,
  Share2: IShare2,
  Shield: IShield,
  ShieldCheck: IShieldCheck,
  ShieldOff: IShieldOff,
  Sliders: ISliders,
  Smartphone: ISmartphone,
  SortAsc: ISortAsc,
  SortDesc: ISortDesc,
  Sparkles: ISparkles,
  Star: IStar,
  StickyNote: IStickyNote,
  Tablet: ITablet,
  Tag: ITag,
  Tags: ITags,
  Target: ITarget,
  Terminal: ITerminal,
  Timer: ITimer,
  Trash: ITrash,
  Trash2: ITrash2,
  TrendingDown: ITrendingDown,
  TrendingUp: ITrendingUp,
  Trophy: ITrophy,
  Unlock: IUnlock,
  Upload: IUpload,
  User: IUser,
  UserCheck: IUserCheck,
  UserCog: IUserCog,
  UserMinus: IUserMinus,
  UserPlus: IUserPlus,
  UserX: IUserX,
  Users: IUsers,
  Video: IVideo,
  VideoOff: IVideoOff,
  Wallet: IWallet,
  Webhook: IWebhook,
  Wifi: IWifi,
  WifiOff: IWifiOff,
  Wrench: IWrench,
  X: IX,
  XCircle: IXCircle,
  Zap: IZap,
};

/** Names in the static map (for tests and the picker). */
export const STATIC_ICON_NAMES: ReadonlySet<string> = new Set(Object.keys(STATIC_ICONS));

// ============================================================================
// Lazy full set
// ============================================================================

/** Icons registered from the lazily loaded full Lucide set. */
const extraIcons = new Map<string, LucideIcon>();
let fullSetLoaded = false;
let fullSetPromise: Promise<void> | null = null;
let registryVersion = 0;
const registryListeners = new Set<() => void>();

function isComponent(value: unknown): value is LucideIcon {
  return value !== null && typeof value === 'object' && '$$typeof' in (value as object);
}

/** Monotonic counter bumped when the full set registers (for useSyncExternalStore). */
export function getIconRegistryVersion(): number {
  return registryVersion;
}

export function subscribeIconRegistry(listener: () => void): () => void {
  registryListeners.add(listener);
  return () => {
    registryListeners.delete(listener);
  };
}

/** Called by ./iconsFull once the full Lucide namespace has loaded. */
export function registerFullIconSet(all: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(all)) {
    if (isComponent(value)) extraIcons.set(key, value);
  }
  fullSetLoaded = true;
  // Misses cached while the set was loading are not final.
  iconCache.clear();
  availableIconsCache = null;
  registryVersion += 1;
  registryListeners.forEach((listener) => listener());
}

/**
 * Load the rest of Lucide (separate chunk). Idempotent. Resolves when the
 * registry is populated; never rejects (a failed load can be retried).
 */
export function loadFullIconSet(): Promise<void> {
  if (fullSetLoaded) return Promise.resolve();
  if (!fullSetPromise) {
    fullSetPromise = import('./iconsFull')
      .then((mod) => registerFullIconSet(mod.FULL_ICON_SET))
      .catch(() => {
        fullSetPromise = null;
      });
  }
  return fullSetPromise;
}

// ============================================================================
// Icon Cache
// ============================================================================
/** Shape of a Lucide component export (PascalCase identifier). */
const LUCIDE_EXPORT_NAME = /^[A-Z][A-Za-z0-9]*$/;

/** Cache for resolved icons to avoid repeated lookups */
const iconCache = new Map<string, LucideIcon | null>();

/** Cache for normalized icon names */
const normalizedNameCache = new Map<string, string>();

// ============================================================================
// Name Normalization
// ============================================================================

/**
 * Convert kebab-case to PascalCase
 * @example kebabToPascal('arrow-left') => 'ArrowLeft'
 */
function kebabToPascal(str: string): string {
  return str
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

/**
 * Convert camelCase to PascalCase
 * @example camelToPascal('arrowLeft') => 'ArrowLeft'
 */
function camelToPascal(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert snake_case to PascalCase
 * @example snakeToPascal('arrow_left') => 'ArrowLeft'
 */
function snakeToPascal(str: string): string {
  return str
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

/**
 * Normalize icon name to PascalCase (Lucide component name format)
 * Handles: kebab-case, camelCase, snake_case, PascalCase
 */
function normalizeIconName(name: string): string {
  // Check cache first
  const cached = normalizedNameCache.get(name);
  if (cached) return cached;

  let normalized: string;

  // Detect format and convert
  if (name.includes('-')) {
    // kebab-case
    normalized = kebabToPascal(name);
  } else if (name.includes('_')) {
    // snake_case
    normalized = snakeToPascal(name);
  } else if (name[0] === name[0].toLowerCase()) {
    // camelCase
    normalized = camelToPascal(name);
  } else {
    // Already PascalCase
    normalized = name;
  }

  // Cache and return
  normalizedNameCache.set(name, normalized);
  return normalized;
}

// ============================================================================
// Icon Resolution
// ============================================================================

/**
 * Resolve a string icon name to a Lucide React component
 *
 * @param iconName - Icon name in any format (kebab-case, camelCase, PascalCase, snake_case)
 * @returns Lucide icon component or null if not found
 *
 * @example
 * resolveIcon('arrow-left')  // => ArrowLeft component
 * resolveIcon('ArrowLeft')   // => ArrowLeft component
 * resolveIcon('arrowLeft')   // => ArrowLeft component
 * resolveIcon('invalid')     // => null
 */
export function resolveIcon(iconName: string | null | undefined): LucideIcon | null {
  // Handle null/undefined/empty
  if (!iconName) return null;

  // Check cache first
  const cached = iconCache.get(iconName);
  if (cached !== undefined) return cached;

  // Normalize name to PascalCase
  const normalizedName = normalizeIconName(iconName);

  // Custom icons first, then the static map, then the lazily loaded full set
  const customIcon = (customIcons as Record<string, unknown>)[normalizedName];
  const icon: unknown =
    customIcon ?? STATIC_ICONS[normalizedName] ?? extraIcons.get(normalizedName);

  // Validate it's actually a component (object with $$typeof for React components)
  // Lucide icons are React.ForwardRefExoticComponent, which are objects, not functions
  const result = isComponent(icon) ? icon : null;

  if (result) {
    iconCache.set(iconName, result);
    return result;
  }

  if (fullSetLoaded || !LUCIDE_EXPORT_NAME.test(normalizedName)) {
    // The full set has been consulted, or the name can never be a Lucide export
    // (e.g. the emoji notifications carry): this miss is final, and it must not
    // fetch the full set.
    iconCache.set(iconName, null);
  } else if (typeof window !== 'undefined') {
    // Not in the static map: it may still be a real Lucide icon (e.g. picked in
    // the admin UI). Fetch the rest once; subscribers re-render when it lands.
    // The miss is NOT cached so the next resolve can succeed.
    void loadFullIconSet();
  }
  return null;
}

/**
 * Check if an icon name is valid (exists in Lucide)
 */
export function isValidIconName(iconName: string | null | undefined): boolean {
  return resolveIcon(iconName) !== null;
}

// ============================================================================
// Icon Listing (for Admin UI)
// ============================================================================

/** Cached list of available icon names */
let availableIconsCache: string[] | null = null;

/**
 * Get list of available Lucide icon names (the static subset until
 * loadFullIconSet() resolves, then every Lucide icon)
 * Returns names in PascalCase format
 *
 * @returns Array of icon names
 */
export function getAvailableIcons(): string[] {
  if (availableIconsCache) return availableIconsCache;

  // Static names plus (once loaded) every Lucide export; keep icon components only.
  // Icons are PascalCase React components, exclude utilities and types
  const candidates = new Set<string>([...Object.keys(STATIC_ICONS), ...extraIcons.keys()]);
  const iconNames = [...candidates].filter((key) => {
    // Skip non-PascalCase names (utilities, types)
    if (key[0] !== key[0].toUpperCase()) return false;

    // Skip known non-icon exports and "Icon" suffix duplicates
    if (key === 'createLucideIcon' || key === 'Icon' || key.endsWith('Icon')) return false;

    // Skip "Lucide" prefixed duplicates
    if (key.startsWith('Lucide')) return false;

    return true;
  });

  const sorted = iconNames.sort();
  // Until the full set loads the list is the static subset: do not pin it.
  if (fullSetLoaded) availableIconsCache = sorted;
  return sorted;
}

/**
 * Search available icons by partial name
 *
 * @param query - Search query
 * @param limit - Maximum number of results (default: 50)
 * @returns Array of matching icon names
 */
export function searchIcons(query: string, limit = 50): string[] {
  const lowerQuery = query.toLowerCase();
  const allIcons = getAvailableIcons();

  return allIcons
    .filter((name) => name.toLowerCase().includes(lowerQuery))
    .slice(0, limit);
}

/**
 * Get commonly used icons (for quick selection in admin UI)
 */
export function getCommonIcons(): string[] {
  return [
    // Navigation
    'Home',
    'Menu',
    'ChevronLeft',
    'ChevronRight',
    'ChevronUp',
    'ChevronDown',
    'ArrowLeft',
    'ArrowRight',

    // Actions
    'Plus',
    'Minus',
    'X',
    'Check',
    'Edit',
    'Trash2',
    'Save',
    'Download',
    'Upload',
    'Share',
    'Copy',

    // Communication
    'MessageSquare',
    'Mail',
    'Phone',
    'Video',
    'Bell',
    'Send',

    // People
    'User',
    'Users',
    'UserPlus',
    'UserCheck',

    // Content
    'File',
    'FileText',
    'Folder',
    'FolderOpen',
    'Image',
    'Film',

    // UI Elements
    'Settings',
    'Cog',
    'Search',
    'Filter',
    'SortAsc',
    'SortDesc',

    // Status
    'AlertCircle',
    'AlertTriangle',
    'Info',
    'HelpCircle',
    'CheckCircle',
    'XCircle',

    // Security
    'Shield',
    'Lock',
    'Unlock',
    'Key',
    'Eye',
    'EyeOff',

    // Business
    'Building',
    'Briefcase',
    'Calendar',
    'Clock',
    'DollarSign',
    'CreditCard',

    // Technology
    'Laptop',
    'Monitor',
    'Smartphone',
    'Wifi',
    'Globe',
    'Database',

    // Charts
    'BarChart',
    'LineChart',
    'PieChart',
    'Activity',
    'TrendingUp',
    'TrendingDown',

    // Maps
    'Map',
    'MapPin',
    'Navigation',
    'Compass',

    // Misc
    'Star',
    'Heart',
    'Bookmark',
    'Tag',
    'Layers',
    'Grid',
    'List',
  ];
}

// ============================================================================
// Icon Categories (for organized admin UI)
// ============================================================================

/**
 * Icon categories for organized selection
 */
export interface IconCategory {
  name: string;
  icons: string[];
}

/**
 * Get icons organized by category
 */
export function getIconCategories(): IconCategory[] {
  return [
    {
      name: 'Navigation',
      icons: [
        'Home',
        'Menu',
        'ChevronLeft',
        'ChevronRight',
        'ChevronUp',
        'ChevronDown',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'CornerUpLeft',
        'CornerUpRight',
        'ExternalLink',
      ],
    },
    {
      name: 'Actions',
      icons: [
        'Plus',
        'Minus',
        'X',
        'Check',
        'Edit',
        'Edit2',
        'Edit3',
        'Trash',
        'Trash2',
        'Save',
        'Download',
        'Upload',
        'Share',
        'Share2',
        'Copy',
        'Clipboard',
        'Refresh',
        'RefreshCw',
        'RotateCw',
        'RotateCcw',
      ],
    },
    {
      name: 'Communication',
      icons: [
        'MessageSquare',
        'MessageCircle',
        'Mail',
        'Phone',
        'PhoneCall',
        'PhoneIncoming',
        'PhoneOutgoing',
        'Video',
        'VideoOff',
        'Bell',
        'BellOff',
        'Send',
        'Inbox',
        'AtSign',
      ],
    },
    {
      name: 'People',
      icons: [
        'User',
        'Users',
        'UserPlus',
        'UserMinus',
        'UserCheck',
        'UserX',
        'Contact',
        'Contact2',
      ],
    },
    {
      name: 'Content',
      icons: [
        'File',
        'FileText',
        'FilePlus',
        'FileMinus',
        'FileCheck',
        'FileX',
        'Folder',
        'FolderOpen',
        'FolderPlus',
        'FolderMinus',
        'Image',
        'Film',
        'Music',
        'Book',
        'BookOpen',
      ],
    },
    {
      name: 'UI Elements',
      icons: [
        'Settings',
        'Settings2',
        'Cog',
        'Search',
        'Filter',
        'SortAsc',
        'SortDesc',
        'Maximize',
        'Minimize',
        'Maximize2',
        'Minimize2',
        'MoreHorizontal',
        'MoreVertical',
      ],
    },
    {
      name: 'Status',
      icons: [
        'AlertCircle',
        'AlertTriangle',
        'AlertOctagon',
        'Info',
        'HelpCircle',
        'CheckCircle',
        'CheckCircle2',
        'XCircle',
        'Loader',
        'Loader2',
      ],
    },
    {
      name: 'Security',
      icons: [
        'Shield',
        'ShieldCheck',
        'ShieldOff',
        'Lock',
        'Unlock',
        'Key',
        'Eye',
        'EyeOff',
        'Fingerprint',
      ],
    },
    {
      name: 'Business',
      icons: [
        'Building',
        'Building2',
        'Briefcase',
        'Calendar',
        'CalendarDays',
        'Clock',
        'DollarSign',
        'CreditCard',
        'Receipt',
        'Wallet',
      ],
    },
    {
      name: 'Technology',
      icons: [
        'Laptop',
        'Monitor',
        'Smartphone',
        'Tablet',
        'Wifi',
        'WifiOff',
        'Globe',
        'Globe2',
        'Database',
        'Server',
        'Cloud',
        'CloudOff',
        'Code',
        'Terminal',
      ],
    },
    {
      name: 'Charts',
      icons: [
        'BarChart',
        'BarChart2',
        'BarChart3',
        'LineChart',
        'PieChart',
        'Activity',
        'TrendingUp',
        'TrendingDown',
        'Percent',
      ],
    },
    {
      name: 'Maps',
      icons: [
        'Map',
        'MapPin',
        'Navigation',
        'Navigation2',
        'Compass',
        'Locate',
        'LocateFixed',
        'LocateOff',
      ],
    },
    {
      name: 'Misc',
      icons: [
        'Star',
        'Heart',
        'Bookmark',
        'Tag',
        'Tags',
        'Layers',
        'Grid',
        'Grid3x3',
        'List',
        'LayoutGrid',
        'LayoutList',
        'Sparkles',
        'Zap',
        'Flame',
      ],
    },
  ];
}

// ============================================================================
// Cache Management
// ============================================================================

/**
 * Clear icon resolution cache
 * Useful for development/testing
 */
export function clearIconCache(): void {
  iconCache.clear();
  normalizedNameCache.clear();
  availableIconsCache = null;
}
